import type { PrV2CommentTarget, PrV2Tab } from './tabs';

import { parsePrV2Tab, PR_V2_TAB_QUERY_PARAM, PR_V2_TABS } from './tabs';

import { buildPrPath } from '@/utils/app-paths';

/**
 * Element-id prefixes of rendered PR comments. A comment's anchor id is `<prefix>-<commentId>`
 * and its body is `<anchor id>-content`. Inline and file-level threads render on the Changes tab,
 * discussion threads (PR comments, reviews and their review comments) on the Discussion tab.
 */
const PR_V2_COMMENT_ANCHOR_PREFIXES = {
  discussion: 'pr-v2-discussion-thread-comment',
  inline: 'pr-v2-inline-comment',
  file: 'pr-v2-file-thread-comment',
} as const;

export type PrV2CommentAnchorKind = keyof typeof PR_V2_COMMENT_ANCHOR_PREFIXES;

const PR_V2_COMMENT_ANCHOR_KINDS = Object.keys(
  PR_V2_COMMENT_ANCHOR_PREFIXES,
) as PrV2CommentAnchorKind[];

export const PR_V2_COMMENT_ID_QUERY_PARAM = 'commentId';

/** The card a rendered comment's thread sits in: inline in the diff, or in a file's thread list. */
export const PR_V2_THREAD_CARD_SELECTOR =
  '[data-testid="pr-v2-inline-thread-card"], [data-testid="pr-v2-thread-card"]';

export function getPrV2CommentAnchorId(kind: PrV2CommentAnchorKind, commentId: string): string {
  return `${PR_V2_COMMENT_ANCHOR_PREFIXES[kind]}-${commentId}`;
}

export function getPrV2CommentAnchorTab(kind: PrV2CommentAnchorKind): PrV2Tab {
  return kind === 'discussion' ? PR_V2_TABS.DISCUSSION : PR_V2_TABS.CHANGES;
}

/** Reads a comment anchor id, or the id of its body, back into its kind and comment id. */
export function parsePrV2CommentAnchorId(
  anchorId: string,
): { kind: PrV2CommentAnchorKind; commentId: string } | null {
  for (const kind of PR_V2_COMMENT_ANCHOR_KINDS) {
    const prefix = `${PR_V2_COMMENT_ANCHOR_PREFIXES[kind]}-`;
    if (anchorId.startsWith(prefix)) {
      const commentId = anchorId.slice(prefix.length).replace(/-content$/, '');
      return commentId ? { kind, commentId } : null;
    }
  }

  return null;
}

/**
 * Anchor ids a Changes-tab comment can render under. A comment id alone does not say whether its
 * thread is inline or file-level, so lookups by id try both.
 */
export function getPrV2ChangesCommentAnchorIds(commentId: string): string[] {
  return [getPrV2CommentAnchorId('inline', commentId), getPrV2CommentAnchorId('file', commentId)];
}

/**
 * Replaces the PR page's URL in place, for comment-link changes that go around nuqs.
 * - Call it only when no nuqs update is pending: nuqs aborts queued updates when it sees an outside
 *   history write.
 * - It writes a null history state so Next's router adopts the URL. With the router's own state the
 *   write is invisible to it, and it later restores the URL it last saw.
 */
export function replacePrV2PageUrl(url: URL): void {
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

/** What a comment link carries: the comment and the tab it opens on. */
export type PrV2CommentLink = Pick<PrV2CommentTarget, 'tab' | 'commentId'>;

/**
 * Points a PR page URL at a comment, keeping its other query params; `parsePrV2CommentLink` reads
 * it back. Discussion links also carry the comment's anchor hash, the same hash the Discussion tab
 * sets when it scrolls to a comment.
 */
export function setPrV2CommentLink(url: URL, { tab, commentId }: PrV2CommentLink) {
  url.searchParams.set(PR_V2_TAB_QUERY_PARAM, tab);
  url.searchParams.set(PR_V2_COMMENT_ID_QUERY_PARAM, commentId);
  url.hash = tab === PR_V2_TABS.DISCUSSION ? getPrV2CommentAnchorId('discussion', commentId) : '';
}

/**
 * Reads the comment a PR page URL points at: a comment anchor hash, or `commentId` on the tab the
 * URL names (Changes by default). Accepts links from before `commentId` was always set.
 */
export function parsePrV2CommentLink(url: URL): PrV2CommentLink | null {
  const anchor = parsePrV2CommentAnchorId(url.hash.replace(/^#/, ''));
  if (anchor) {
    return { tab: getPrV2CommentAnchorTab(anchor.kind), commentId: anchor.commentId };
  }

  const commentId = url.searchParams.get(PR_V2_COMMENT_ID_QUERY_PARAM)?.trim();
  if (!commentId) {
    return null;
  }

  const tab = parsePrV2Tab(url.searchParams.get(PR_V2_TAB_QUERY_PARAM)) ?? PR_V2_TABS.CHANGES;
  return { tab, commentId };
}

/** Relative path of the PR page, opened on a comment. */
export function buildPrV2CommentPath(
  pr: { owner: string; repo: string; number: number },
  link: PrV2CommentLink,
): string {
  // The base only lets URL parse the relative path; it is dropped from the result.
  const url = new URL(buildPrPath(pr), 'https://pr-v2.invalid');
  setPrV2CommentLink(url, link);
  return `${url.pathname}${url.search}${url.hash}`;
}
